import React, { useMemo } from "react";
import { AlertTriangle, Bed, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { accommodationTotals, newRoom } from "@/pages/cotizador/shared";
import { CotAccommodationCard } from "@/pages/cotizador/CotAccommodationCard";

export function CotAccommodations({ accommodations, roomConfig, numTravelers, markup, startDate, endDate, onChange }) {
  const list = accommodations || [];

  const buildDefaultRooms = () => {
    const cfg = (roomConfig && roomConfig.length) ? roomConfig : [{ room_type: "doble", pax: 2, quantity: 1 }];
    return cfg.flatMap((rc) => Array.from({ length: rc.quantity || 1 }, () => newRoom({ room_type: rc.room_type, pax: rc.pax })));
  };
  const recompute = (a) => {
    if ((a.rooms || []).length > 0) { const t = accommodationTotals(a); return { ...a, cost_tax_incl: t.incl, cost_tax_excl: t.excl }; }
    return a;
  };
  const add = () => onChange([...list, recompute({
    id: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`),
    name: "", provider_id: null, product_id: null, city: "", country: "",
    date_from: startDate || null, date_to: endDate || null,
    rooms: buildDefaultRooms(), cost_tax_excl: 0, cost_tax_incl: 0, currency: "EUR", board: "bb", notes: "",
  })]);

  const overlaps = useMemo(() => {
    const accs = list.filter((a) => a.name && a.date_from && a.date_to);
    const out = [];
    for (let i = 0; i < accs.length; i++) for (let j = i + 1; j < accs.length; j++) {
      const a = accs[i], b = accs[j];
      const aF = new Date(a.date_from), aT = new Date(a.date_to), bF = new Date(b.date_from), bT = new Date(b.date_to);
      if ([aF, aT, bF, bT].some((d) => isNaN(d))) continue;
      if (aF < bT && bF < aT) {
        const days = Math.max(0, Math.round((Math.min(aT, bT) - Math.max(aF, bF)) / 86400000));
        if (days > 0) out.push({ a, b, days });
      }
    }
    return out;
  }, [accommodations]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3" data-testid="cot-accommodations">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-base font-semibold"><Bed className="h-4 w-4 text-primary" /> Alojamientos (sumario)</h2>
        <Button size="sm" variant="outline" onClick={add} data-testid="cot-add-accommodation"><Plus className="mr-1.5 h-4 w-4" /> Añadir alojamiento</Button>
      </div>

      {overlaps.length > 0 && (
        <div className="rounded-md border border-destructive bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="cot-acc-overlap">
          <div className="mb-1 flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" />{overlaps.length === 1 ? "Solapamiento detectado" : `${overlaps.length} solapamientos detectados`}</div>
          <ul className="list-disc space-y-0.5 pl-5 text-[12px]">
            {overlaps.map((o, i) => (
              <li key={i}><span className="font-semibold">{o.a.name}</span> ({o.a.date_from}→{o.a.date_to}) solapa con <span className="font-semibold">{o.b.name}</span> ({o.b.date_from}→{o.b.date_to}) — {o.days} {o.days === 1 ? "día" : "días"}.</li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-lg border bg-card">
        {list.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">Opcional. Añade alojamientos resumidos por estancia (con habitaciones y precio por noche). También puedes crearlos desde cada día con el botón «Hotel».</p>
        ) : list.map((a, idx) => (
          <CotAccommodationCard
            key={a.id} a={a} roomConfig={roomConfig} numTravelers={numTravelers} markup={markup}
            onChange={(updated) => onChange(list.map((x, i) => (i === idx ? updated : x)))}
            onRemove={() => onChange(list.filter((_, i) => i !== idx))}
            tid={`cot-acc-${idx}`}
          />
        ))}
      </div>
    </div>
  );
}
